import { Button, Dialog } from "@rio-cloud/rio-uikit";
import { useEffect, useState } from "react";

interface ISearchAutocompleteProps {
  id: string;
  placeholder: string;
  fetchOptions: (searchTerm: string) => Promise<any[]>;
  optionLabel: { [key: string]: string };
  onSelectedOption: (e: any) => void;
  validateOption?: (option: any) => Promise<boolean>;
  overlay?: boolean;
  overlayClassName?: string;
  disabled?: boolean;
  optionRenderer?: (option: any) => React.ReactNode;
  valueRenderer?: (option: any) => string;
  defaultValue?: any;
  onClear?: () => void;
  allowManualAdd?: boolean;
}

const SearchAutocomplete = (props: ISearchAutocompleteProps) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [optionSelected, setOptionSelected] = useState<any | null>(null);

  const [showAddValueDialog, setShowAddValueDialog] = useState(false);
  const [addValue, setAddValue] = useState<any | null>(null);

  useEffect(() => {
    if (props.defaultValue) {
      setOptionSelected(props.defaultValue);
    }
  }, [props.defaultValue]);

  useEffect(() => {
    if (!searchTerm?.trim()) {
      setOptions([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await props.fetchOptions(searchTerm);
        setOptions(response);
      } catch (error) {
        console.error('Erro ao buscar opções:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchTerm, props.fetchOptions]);

  const loadingElement = () => {
    return (
      <div className=" w-full">
        <div className="spinnerInfoBox display-flex justify-content-center align-items-center height-100pct">
          <div className="spinner">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div className="spinnerText">Buscando...</div>
        </div>
      </div>
    );
  };

  return (
    <>
      {optionSelected ? (
        <input
          id={`${props.id}-selected`}
          type="text"
          className="form-control w-full md:min-w-[300px]"
          placeholder={props.placeholder}
          value={
            props.valueRenderer
              ? props.valueRenderer(optionSelected)
              :
              Object.entries(props.optionLabel)
                .map(
                  ([key, value]) => optionSelected[key as keyof typeof optionSelected] ? `${value}: ${optionSelected[key as keyof typeof optionSelected]}` : null
                )
                .filter(Boolean)
                .join(" - ")}
          onChange={(e) => {
            e.target.blur();
            const nextInput = e.target.form?.querySelector(
              `input#${props.id}`
            ) as HTMLInputElement;
            nextInput?.focus();
            setOptionSelected(null);
            setSearchTerm(e.target.value[e.target.value.length - 1]);
            if (e.target.value === '') props.onClear?.();
          }}
          disabled={props.disabled}
          size={(
            props.valueRenderer
              ? props.valueRenderer(optionSelected)
              : Object.entries(props.optionLabel)
                .map(([key, value]) =>
                  optionSelected[key as keyof typeof optionSelected]
                    ? `${value}: ${optionSelected[key as keyof typeof optionSelected]}`
                    : null
                )
                .filter(Boolean)
                .join(" - ")
          )?.length || 1}
        />
      ) : (
        <>
          <input
            id={props.id}
            type="text"
            className="form-control w-full md:min-w-[300px]"
            placeholder={props.placeholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value === '') props.onClear?.();
            }}
            disabled={props.disabled}
            size={searchTerm.length || 1} />
          {searchTerm && (
            <div
              className={`bg-white rounded shadow p-2 mt-2 z-10 w-full max-h-80 overflow-y-auto ${props.overlay
                  ? `absolute top-100 ${props.overlayClassName}`
                  : ""
                }`}
            >
              {loading && loadingElement()}
              {!loading &&
                options.map((optionEl, index) => (
                  <div
                    key={index}
                    className="p-2 mb-2 cursor-pointer"
                    onClick={async () => {
                      if (props.validateOption) {
                        const isValid = await props.validateOption(optionEl);
                        if (!isValid) return;
                      }

                      setSearchTerm("");
                      props.onSelectedOption(optionEl);
                    }}
                  >
                    {props.optionRenderer
                      ? props.optionRenderer(optionEl)
                      : Object.entries(props.optionLabel)
                        .map(
                          ([key, value]) => optionEl[key as keyof typeof optionEl] ? `${value}: ${optionEl[key as keyof typeof optionEl]}` : null
                        )
                        .filter(Boolean)
                        .join(" - ")}
                  </div>
                ))}

              {!loading && options.length === 0 && (
                <div className="p-2 mb-2 cursor-block">
                  Nenhum resultado encontrado
                </div>
              )}

              {!loading && props.allowManualAdd && <div
                className="p-2 mb-2 cursor-pointer text-color-primary"
                onClick={() => {
                  setShowAddValueDialog(true);
                }}
              >
                Adicionar valor manualmente
              </div>}
            </div>
          )}
        </>
      )}

      <Dialog
        show={showAddValueDialog}
        onClose={() => setShowAddValueDialog(false)}
        title="Adicionar valor"
        bsSize="sm"
        showCloseButton
        footer={
          <div>
            <Button bsStyle="primary" onClick={() => {
              props.onSelectedOption(addValue);
              setAddValue("");
              setShowAddValueDialog(false);
            }}>Adicionar</Button>
          </div>
        }
        body={<div>
          <div className="form-group">
            {
              props.optionLabel && Object.entries(props.optionLabel).map(([key, value]) => (
                <div className="form-group">
                  <label htmlFor="addValue">{value}</label>
                  <input
                    className="form-control"
                    placeholder={value}
                    value={addValue?.[key]}
                    onChange={(e) => setAddValue({ ...addValue, [key]: e.target.value })}
                  />
                </div>
              ))}
          </div>
        </div>}
      />
    </>
  );
};

export default SearchAutocomplete;
